import type {
  ParcelSummary,
  ParcelDetail,
  GeoJSONFeatureCollection,
  FilterState,
  MapBounds,
  Stats,
} from '../types/filters';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// Valid coordinate ranges for zachodniopomorskie/lubuskie regions
const BOUNDS_LIMITS = {
  min_lat: 49,
  max_lat: 55,
  min_lng: 14,
  max_lng: 17,
};

/**
 * Clamp map bounds to the valid API range.
 * Returns null if the clamped bounds have no valid area (viewport completely outside region).
 */
function clampBounds(bounds: MapBounds): MapBounds | null {
  const clamped: MapBounds = {
    min_lat: Math.max(bounds.min_lat, BOUNDS_LIMITS.min_lat),
    max_lat: Math.min(bounds.max_lat, BOUNDS_LIMITS.max_lat),
    min_lng: Math.max(bounds.min_lng, BOUNDS_LIMITS.min_lng),
    max_lng: Math.min(bounds.max_lng, BOUNDS_LIMITS.max_lng),
  };

  // Check if there's any valid area after clamping
  if (clamped.min_lat >= clamped.max_lat || clamped.min_lng >= clamped.max_lng) {
    return null;
  }

  return clamped;
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.error?.message || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchParcels(
  bounds: MapBounds,
  filters: FilterState
): Promise<{ parcels: ParcelSummary[]; count: number }> {
  const params = new URLSearchParams({
    min_lat: String(bounds.min_lat),
    max_lat: String(bounds.max_lat),
    min_lng: String(bounds.min_lng),
    max_lng: String(bounds.max_lng),
  });

  if (filters.voivodeship) {
    params.set('voivodeship', filters.voivodeship);
  }
  if (filters.tender_type) {
    params.set('tender_type', filters.tender_type);
  }
  if (filters.min_price !== null) {
    params.set('min_price', String(filters.min_price));
  }
  if (filters.max_price !== null) {
    params.set('max_price', String(filters.max_price));
  }

  return fetchJson(`${API_BASE_URL}/api/parcels?${params}`);
}

export async function fetchParcelsGeoJSON(
  bounds: MapBounds,
  filters: FilterState
): Promise<GeoJSONFeatureCollection> {
  // Clamp bounds to valid API range
  const clampedBounds = clampBounds(bounds);

  // If viewport is completely outside the valid region, return empty result
  if (!clampedBounds) {
    return { type: 'FeatureCollection', features: [] };
  }

  const params = new URLSearchParams({
    min_lat: String(clampedBounds.min_lat),
    max_lat: String(clampedBounds.max_lat),
    min_lng: String(clampedBounds.min_lng),
    max_lng: String(clampedBounds.max_lng),
  });

  if (filters.voivodeship) {
    params.set('voivodeship', filters.voivodeship);
  }
  if (filters.tender_type) {
    params.set('tender_type', filters.tender_type);
  }

  return fetchJson(`${API_BASE_URL}/api/parcels/geojson?${params}`);
}

export async function fetchParcelDetails(id: string): Promise<ParcelDetail> {
  const data = await fetchJson<{ parcel: ParcelDetail }>(
    `${API_BASE_URL}/api/parcels/${id}`
  );
  return data.parcel;
}

export async function fetchStats(): Promise<Stats> {
  return fetchJson(`${API_BASE_URL}/api/stats`);
}

export type RecentParcel = {
  id: string;
  parcel_number: string;
  district: string | null;
  commune: string | null;
  tender_type: string | null;
  price: number | null;
  tender_date: string | null;
  source_name: string;
  created_at: string;
  lat: number;
  lng: number;
};

export async function fetchRecentParcels(
  limit = 10
): Promise<{ parcels: RecentParcel[]; count: number }> {
  return fetchJson(`${API_BASE_URL}/api/parcels/recent?limit=${limit}`);
}
